import { config } from './config.js';

// Thin wrapper around @clerk/clerk-js. Loaded lazily so that builds without
// a key (offline / pre-launch) don't fail. When no key is configured the app
// runs in "anonymous" mode — IDB-only, no cloud sync.

let clerk = null;
let loadPromise = null;
let storageOwner = 'guest';
let accountChanged = false;
let storageLocked = false;
export function lockAccountStorage() { storageLocked = true; }
function invalidateAccount() {
  accountChanged = true;
  window.dispatchEvent(new Event('sayermore:account-changing'));
  document.documentElement.style.visibility = 'hidden';
  window.location.reload();
}

export function accountStorageKey(key) { return `${key}:${storageOwner}`; }
export function accountIsCurrent() { return !accountChanged && (getUser()?.id || 'guest') === storageOwner; }

const listeners = new Set();

export function isConfigured() {
  return Boolean(config.clerkKey);
}

export async function initAuth() {
  if (!isConfigured()) return null;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    const mod = await import('@clerk/clerk-js');
    const Clerk = mod.Clerk || mod.default;
    clerk = new Clerk(config.clerkKey);
    const landingUrl = window.location.pathname + window.location.search;
    await clerk.load({
      signInUrl: undefined,
      signInFallbackRedirectUrl: landingUrl,
      signInForceRedirectUrl: landingUrl,
      signUpFallbackRedirectUrl: landingUrl,
      signUpForceRedirectUrl: landingUrl,
    });
    const owner = getUser()?.id || 'guest';
    if (storageLocked && owner !== storageOwner) { invalidateAccount(); throw new Error('Account changed during initialization'); }
    storageOwner = owner;
    cleanupAuthUrl();
    clerk.addListener(() => {
      if ((getUser()?.id || 'guest') !== storageOwner) {
        invalidateAccount();
        return;
      }
      cleanupAuthUrl();
      listeners.forEach((cb) => cb(getUser()));
    });
    return clerk;
  })();
  return loadPromise;
}

// After Clerk redirects back from OAuth, the URL may carry handshake/status
// markers (e.g. ?__clerk_handshake=...). Strip them with replaceState so the
// browser back button doesn't return the user to a Clerk callback URL.
function cleanupAuthUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let dirty = false;
  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('__clerk') || key === '__client_uat') {
      url.searchParams.delete(key);
      dirty = true;
    }
  }
  if (url.hash && url.hash.includes('__clerk')) {
    url.hash = '';
    dirty = true;
  }
  if (dirty) {
    const cleaned = url.pathname + (url.search ? url.search : '') + url.hash;
    history.replaceState(history.state, '', cleaned);
  }
}

export function getUser() {
  return clerk?.user || null;
}

export async function getToken() {
  const session = clerk?.session;
  if (!session) return null;
  try {
    return await session.getToken();
  } catch {
    return null;
  }
}

// Returns true if the Clerk modal was opened, false if the SDK couldn't be
// reached. Callers should toast on false so the user knows their tap
// registered. Defensive: if clerk isn't loaded yet (initAuth still in
// flight, or failed), try once more with a short timeout before giving up.
export async function signIn() {
  if (!clerk) {
    try {
      await Promise.race([
        initAuth(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('signin_timeout')), 8000)),
      ]);
    } catch {
      return false;
    }
  }
  if (!clerk) return false;
  clerk.openSignIn({});
  return true;
}

export function signOut() {
  if (!clerk) return;
  clerk.signOut();
}

export function onAuthChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
